import { configureStore } from '@reduxjs/toolkit';
import { createInjectorsEnhancer } from 'redux-injectors';
import createSagaMiddleware from 'redux-saga';
import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import rootSaga from '../sagas';
import { createReducer } from './reducers';
import { socketMiddleware } from 'core/middleware/socket';
import { InjectedReducersType } from 'utils/types/injector-typings';
import { AxiosWrapper } from 'core/services/axios.wrapper';
import packageJson from '../../../package.json';
import { env } from 'utils/env';
import { selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectAction } from '../selectors/product/action/action';
import { selectModule } from '../selectors/product/module';

export const history = createBrowserHistory();
const STORE_GLOBAL_KEY = '__HOWIO_APP_STORE__';

export const injectedReducers: InjectedReducersType = {};
export const reducer: any = createReducer(injectedReducers);

async function onError(error) {
  console.error(error);
  await AxiosWrapper.post(`/api/error`, { type: 0, stacktrace: JSON.stringify(error), message: error.message });
}

export function configureAppStore() {
  const reduxSagaMonitorOptions = {
    onError,
  };
  const sagaMiddleware = createSagaMiddleware(reduxSagaMonitorOptions);
  const { run: runSaga } = sagaMiddleware;

  if (import.meta.env.DEV) console.info(`version: ${packageJson.version}`);

  // Create the store with saga middleware
  const middlewares: any = [sagaMiddleware, routerMiddleware(history), socketMiddleware()];

  //if (isSafari && process.env.NODE_ENV === 'development') middlewares.push(logger);

  const enhancers: any = [
    createInjectorsEnhancer({
      createReducer,
      runSaga,
    }),
  ];

  // Disable remote-redux-devtools for Vite compatibility
  // if (env.REMOTE === 'true') {
  //   const devToolsEnhancer = await import('remote-redux-devtools').then(m => m.default);
  //   enhancers.push(devToolsEnhancer({ realtime: true, name: 'how.io', hostname: 'localhost', port: 8000 }));
  // }

  const store = configureStore({
    reducer,
    middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(middlewares),
    devTools:
      !import.meta.env.PROD || env.DEVTOOLS === 'true'
        ? {
            name: `how.io (${import.meta.env.MODE})`,
            shouldHotReload: false,
            maxAge: 50,
            stateSanitizer: (state: any) => {
              const ent = state?.entities;
              if (!ent) return state;
              // Truncate large entity collections so DevTools stays fast
              const sanitized: Record<string, any> = {};
              for (const key of Object.keys(ent)) {
                const col = ent[key];
                const ids = col ? Object.keys(col) : [];
                if (ids.length > 50) {
                  sanitized[key] = `<<${ids.length} items — truncated>>`;
                } else {
                  sanitized[key] = col;
                }
              }
              return { ...state, entities: sanitized };
            },
          }
        : false,
    enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(enhancers),
  });

  sagaMiddleware.run(rootSaga);

  if (typeof window !== 'undefined') {
    (window as any).logActionFeed = () => {
      const feedState = selectActionFeed(store.getState() as any);
      console.log('actionFeed', feedState);
      return feedState;
    };
    (window as any).logAction = () => {
      const feedState = selectAction(store.getState() as any, undefined);
      console.log('action', feedState);
      return feedState;
    };
    (window as any).logModule = () => {
      const feedState = selectModule(store.getState() as any, undefined);
      console.log('module', feedState);
      return feedState;
    };
  }

  return store;
}

const globalStoreContainer = globalThis as typeof globalThis & {
  [STORE_GLOBAL_KEY]?: ReturnType<typeof configureAppStore>;
};

export const store =
  import.meta.env.DEV && globalStoreContainer[STORE_GLOBAL_KEY]
    ? globalStoreContainer[STORE_GLOBAL_KEY]
    : configureAppStore();

if (import.meta.env.DEV) {
  globalStoreContainer[STORE_GLOBAL_KEY] = store;
}

export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
